/**
 * Per-device lap list for circuit events
 * (backend `/api/fleet/events/<id>/laps`).
 *
 * Live events merge `lap_completed` SSE frames on top of the REST seed
 * so the lap list grows in real time; replay pages fetch once. The
 * session-end recompute on the backend is authoritative — a refetch on
 * `lap_completed` for OTHER devices is skipped (cheap client-side
 * filter), and duplicates are collapsed by (session_id, lap_number).
 */

import { useEffect, useMemo, useState } from 'react';

import api from '../services/api';
import { Lap, listEventLaps } from '../services/fleet';
import { FleetEvent } from '../types';

export interface EventLapsState {
  laps: Lap[];
  loading: boolean;
}

export function useEventLaps(
  eventId: number | null,
  deviceId: string | null,
  /** Subscribe to lap_completed SSE (live pages only). */
  live: boolean,
): EventLapsState {
  const [laps, setLaps] = useState<Lap[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLaps([]);
    if (eventId == null || !deviceId) return;
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        const data = await listEventLaps(eventId, deviceId);
        if (!cancelled) setLaps(data);
      } catch (err) {
        console.warn('[useEventLaps] fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, deviceId]);

  useEffect(() => {
    if (!live || eventId == null || !deviceId) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type !== 'lap_completed') return;
      if (evt.event_id !== eventId || evt.device_id !== deviceId) return;
      setLaps((prev) => {
        if (
          prev.some(
            (l) => l.session_id === evt.session_id && l.lap_number === evt.lap_number,
          )
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            lap_number: evt.lap_number,
            event_id: evt.event_id,
            device_id: evt.device_id,
            session_id: evt.session_id,
            start_utc_ms: evt.start_utc_ms,
            end_utc_ms: evt.end_utc_ms,
            duration_ms: evt.duration_ms,
            end_lat: null,
            end_lon: null,
            source: 'live',
            // Circuit Engine refinement never rides the SSE frame — live
            // laps are unrefined until the session-end recompute.
            sigma_ms: null,
            confidence: null,
            method: null,
            chord_delta_ms: null,
          },
        ];
      });
    });
    return () => unsub();
  }, [live, eventId, deviceId]);

  // CHRONOLOGICAL order — L1 is the device's oldest lap, the last entry
  // its newest. The previous session_id.localeCompare sort interleaved
  // sessions by id TEXT (field bug: yesterday's laps rendered after
  // tonight's because "ph-7…" > "ph-2…"). lap_number tiebreak only
  // matters for identical timestamps (never in practice).
  const sorted = useMemo(
    () =>
      [...laps].sort(
        (a, b) => a.start_utc_ms - b.start_utc_ms || a.lap_number - b.lap_number,
      ),
    [laps],
  );

  return { laps: sorted, loading };
}

export default useEventLaps;
